// F-02 / F-04 — Task urgency + visit-progress helpers
//
// Pure, read-only helpers used by the home Task Queue panel. They classify
// tasks by deadline urgency, sort/group them, and compute a linked site
// visit's task-completion percentage. No task data or status is mutated here.

// Task status enum (see services/Enums/taskStatusesEnum.js):
// 1 Assigned, 2 Accepted, 3 Completed, 4 On hold, 5 Archived
export const TASK_STATUS_COMPLETED = 3;
export const TASK_STATUS_ARCHIVED = 5;

const startOfToday = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

const startOfDay = (value) => {
  const d = new Date(value);
  if (isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

// A task is "active" (belongs in the action queue) when it is not finished.
export const isActiveTask = (task) =>
  Number(task?.status) !== TASK_STATUS_COMPLETED &&
  Number(task?.status) !== TASK_STATUS_ARCHIVED;

// Urgency category + a numeric order for sorting (lower = more urgent).
export const getTaskUrgency = (task) => {
  const due = startOfDay(task?.deadline);
  if (!due) return { category: "none", order: 3, label: "No deadline" };

  const today = startOfToday();
  if (due.getTime() < today.getTime())
    return { category: "overdue", order: 0, label: "Overdue" };
  if (due.getTime() === today.getTime())
    return { category: "today", order: 1, label: "Due today" };
  return { category: "upcoming", order: 2, label: "Upcoming" };
};

// Sort by urgency bucket, then by soonest deadline within the bucket.
export const sortTasksByUrgency = (tasks = []) =>
  [...tasks].sort((a, b) => {
    const ua = getTaskUrgency(a);
    const ub = getTaskUrgency(b);
    if (ua.order !== ub.order) return ua.order - ub.order;
    const da = a?.deadline ? new Date(a.deadline).getTime() : Infinity;
    const db = b?.deadline ? new Date(b.deadline).getTime() : Infinity;
    return da - db;
  });

// Group tasks under their linked site visit so a user sees all outstanding
// actions for one visit together (F-02). Tasks with no linked visit are
// grouped under a single "Unassigned to a visit" bucket.
export const groupTasksByVisit = (tasks = []) => {
  const groups = new Map();
  tasks.forEach((task) => {
    const visitId = task?.taskSite?.id || "__none__";
    if (!groups.has(visitId)) {
      groups.set(visitId, {
        visitId,
        visitName: task?.taskSite?.name || "Not linked to a visit",
        visitIdentifier: task?.taskSite?.identifier || null,
        tasks: [],
      });
    }
    groups.get(visitId).tasks.push(task);
  });
  return Array.from(groups.values());
};

// Compute each visit's task-completion % from the full set of project tasks.
// Returns a Map keyed by site visit id → { total, completed, percent }.
// (F-04 "stage-progress context" reuses this same calculation.)
export const computeVisitCompletion = (allTasks = []) => {
  const byVisit = new Map();
  allTasks.forEach((task) => {
    const visitId = task?.taskSite?.id;
    if (!visitId) return;
    if (!byVisit.has(visitId)) byVisit.set(visitId, { total: 0, completed: 0 });
    const entry = byVisit.get(visitId);
    entry.total += 1;
    if (Number(task?.status) === TASK_STATUS_COMPLETED) entry.completed += 1;
  });

  const result = new Map();
  byVisit.forEach((value, visitId) => {
    result.set(visitId, {
      ...value,
      percent: value.total === 0 ? 0 : Math.round((value.completed / value.total) * 100),
    });
  });
  return result;
};
